// Token entry. Section: token. Job: unlock the dashboard with the operator
// token. Message: paste the token, it equals the server's DASHBOARD_TOKEN.
// Action: Masuk. Container: card (JEV ui.region_gate), recipe core.tpl.login
// (JEV ui.component_recipe): from 640px one 400-wide card centered on the
// page, below 640px the form sits on the page with no card.
//
// Hand-written inside the kit Section: the kit has no auth composition.
// Signed out, there is nothing to navigate to yet, so the screen brings its
// own chrome (a wordmark bar, no destinations, so no tab reads as current)
// and its main opts out of the app-shell rule with data-jal-shell="none".
import { useId, useRef, useState, type FormEvent } from "react";
import { Section } from "@bot-whatsapp/ui";
import { CodeChip, Notice } from "./ui";

export interface TokenScreenProps {
  /** Resolves true when the token is accepted (B2: GET /dashboard/session). */
  validate: (token: string) => Promise<boolean>;
  onAccepted: (token: string) => void;
  /** Shown above the form when the previous token stopped working. */
  expired?: boolean;
}

type Failure = "rejected" | "short" | "unreachable" | null;

export function TokenScreen({ validate, onAccepted, expired }: TokenScreenProps) {
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<Failure>(null);
  const headingId = useId();
  const inputId = useId();
  const hintId = useId();
  const inputRef = useRef<HTMLInputElement>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    const value = token.trim();
    if (value.length < 32) {
      setFailure("short");
      inputRef.current?.focus();
      return;
    }
    setBusy(true);
    setFailure(null);
    try {
      if (await validate(value)) {
        onAccepted(value);
        return;
      }
      setFailure("rejected");
    } catch {
      setFailure("unreachable");
    } finally {
      setBusy(false);
    }
    // The danger notice announces itself (role alert); focus returns to the
    // field so the operator can paste again at once.
    inputRef.current?.focus();
  }

  const invalid = failure === "rejected" || failure === "short";

  return (
    <>
      <header className="dash-auth-bar">
        <p className="dash-auth-brand">Sistem Data Relawan</p>
      </header>
      <main id="main" className="dash-auth-main" data-jal-shell="none">
        <Section id="masuk" className="dash-screen dash-auth" composition="custom" variant="auth" labelledBy={headingId}>
          <div className="dash-auth-panel">
            <h1 id={headingId} className="kit-heading">
              Masuk ke dasbor
            </h1>
            <p className="kit-body">
              Tempel token dasbor. Nilainya sama dengan <CodeChip>DASHBOARD_TOKEN</CodeChip> di server bot.
            </p>
            {/* The heading stays on the page ground; only the form is boxed. */}
            <div className="dash-auth-card">
              <div className="dash-auth-notice">
                {expired && !failure ? (
                  <Notice tone="warning" title="Token tidak berlaku lagi">
                    Server menolak token yang tersimpan. Masukkan token yang sekarang aktif.
                  </Notice>
                ) : null}
                {failure === "rejected" ? (
                  <Notice tone="danger" title="Token ditolak">
                    Periksa kembali token. Nilainya harus sama persis dengan <CodeChip>DASHBOARD_TOKEN</CodeChip> di server.
                  </Notice>
                ) : null}
                {failure === "unreachable" ? (
                  <Notice tone="danger" title="Server tidak dapat dihubungi">
                    Token belum diperiksa. Pastikan server bot berjalan, lalu coba lagi.
                  </Notice>
                ) : null}
              </div>
              <form className="dash-auth-form" onSubmit={submit} noValidate>
                <div className="field">
                  <label htmlFor={inputId}>Token dasbor</label>
                  <input
                    ref={inputRef}
                    id={inputId}
                    type="password"
                    name="token"
                    autoComplete="current-password"
                    spellCheck={false}
                    value={token}
                    onChange={(e) => {
                      setToken(e.target.value);
                      if (failure === "short") setFailure(null);
                    }}
                    aria-invalid={invalid || undefined}
                    aria-describedby={hintId}
                    required
                    minLength={32}
                  />
                  <p id={hintId} className="field-hint" data-state={failure === "short" ? "error" : undefined}>
                    {failure === "short" ? "Token terlalu pendek. Token berisi minimal 32 karakter." : "Minimal 32 karakter."}
                  </p>
                </div>
                <button type="submit" className="btn-primary btn-block" aria-busy={busy || undefined}>
                  Masuk
                </button>
              </form>
            </div>
          </div>
        </Section>
      </main>
    </>
  );
}
